import React, { useState, useRef, useEffect } from 'react';

const ToggleSwitch = ({ enabled, onChange, disabled = false }) => {
  return (
    <div
      className={`toggle-switch ${enabled ? 'enabled' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={disabled ? undefined : onChange}
      style={{
        width: '44px',
        height: '24px',
        backgroundColor: enabled ? '#7C5832' : '#ccc',
        borderRadius: '12px',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.3s ease',
        opacity: disabled ? 0.6 : 1
      }}
    >
      <div
        className="toggle-thumb"
        style={{
          width: '20px',
          height: '20px',
          backgroundColor: 'white',
          borderRadius: '50%',
          position: 'absolute',
          top: '2px',
          left: enabled ? '22px' : '2px',
          transition: 'left 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      />
    </div>
  );
};

// Main Cookie Policy Modal Component
const CookiePolicyModal = ({ darkMode = false }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [preferences, setPreferences] = useState({
    doNotSell: false,
    strictlyNecessary: true, // Always true and disabled
    marketing: false,
    analytics: false,
    performance: false,
    socialMedia: false
  });

  const modalRef = useRef(null);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      setPreferences(prev => ({
        ...prev,
        ...JSON.parse(savedPreferences)
      }));
    }
  }, []);

  const handleOpen = () => {
    setModalIsOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const handleClose = () => {
    setModalIsOpen(false);
    document.body.style.overflow = 'unset'; // Restore scrolling
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleStrictlyNecessaryOnly = () => {
    const strictOnlyPrefs = {
      doNotSell: true,
      strictlyNecessary: true,
      marketing: false,
      analytics: false,
      performance: false,
      socialMedia: false
    };
    setPreferences(strictOnlyPrefs);
    localStorage.setItem('cookiePreferences', JSON.stringify(strictOnlyPrefs));
    handleClose();
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    handleClose();
    // You can add analytics tracking here if needed
    console.log('Cookie preferences saved:', preferences);
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && modalIsOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modalIsOpen]);

  return (
    <>
      <button
        className="cookie-policy-trigger"
        onClick={handleOpen}
        style={{
          position: 'fixed',
          backgroundColor: 'transparent',
          bottom: '60px',
          right: '30px',
          color: '#ffffff',
          fontSize: '12px',
          cursor: 'pointer',
          zIndex: 998,
          border: 'none',
          padding: 0,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          boxShadow: 'none',
          transform: 'none',
          transition: 'none',
          textDecoration: 'underline'

        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = 'none';
        }}
      >
        Cookie Policy
      </button>

      {/* Modal */}
      {modalIsOpen && (
        <div
          className="cookie-modal-overlay"
          onClick={handleOverlayClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <div
            ref={modalRef}
            className={`cookie-modal ${darkMode ? 'dark-theme' : ''}`}
            onClick={handleModalClick}
            style={{
              background: darkMode ? '#1a1a1a' : '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 100000,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            {/* Header */}
            <div
              className="cookie-modal-header"
              style={{
                padding: '20px 24px 16px',
                borderBottom: `1px solid ${darkMode ? '#333' : '#e5e7eb'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: darkMode ? '#ffffff' : '#111827'
              }}>
                cookie preferences
              </h2>
              <button
                className="cookie-modal-close"
                onClick={handleClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = darkMode ? '#ffffff' : '#000000';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = darkMode ? '#9ca3af' : '#6b7280';
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div
              className="cookie-modal-content"
              style={{
                padding: '20px 24px',
                flex: 1,
                overflowY: 'auto',
                color: darkMode ? '#e5e7eb' : '#374151'
              }}
            >
              <p style={{
                marginBottom: '24px',
                lineHeight: '1.6',
                fontSize: '14px'
              }}>
                SendNow uses different categories of cookies to provide, protect, improve and
                promote our website and services. Information about these categories and their
                purposes is provided below. For more information, please see our{' '}
                <a
                  href="https://www.sendnow.live/privacy-policy"
                  style={{
                    color: '#7C5832',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="/cookie-faq"
                  style={{
                    color: '#7C5832',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  Cookie FAQ page
                </a>.
              </p>
              <div>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: darkMode ? '#ffffff' : '#111827'
                }}>
                  Cookie preferences
                </h3>

                {/* Cookie options */}
                {[
                  { key: 'strictlyNecessary', label: 'Strictly necessary', disabled: true },
                  { key: 'marketing', label: 'General marketing and advertising' },
                  { key: 'analytics', label: 'Analytics' },
                  { key: 'performance', label: 'Performance and functionality' },
                  { key: 'socialMedia', label: 'Social media advertising' }
                ].map((option) => (
                  <div
                    key={option.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: `1px solid ${darkMode ? '#333' : '#e5e7eb'}`
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: option.disabled ? 0.6 : 1
                      }}>
                        {option.label}
                      </span>
                    </div>
                    <ToggleSwitch
                      enabled={preferences[option.key]}
                      onChange={option.disabled ? undefined : () => handleToggle(option.key)}
                      disabled={option.disabled}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div
              className="cookie-modal-footer"
              style={{
                padding: '16px 24px 20px',
                borderTop: `1px solid ${darkMode ? '#333' : '#e5e7eb'}`,
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}
            >
              <button
                className="cookie-button secondary"
                onClick={handleStrictlyNecessaryOnly}
                style={{
                  background: 'transparent',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  color: darkMode ? '#e5e7eb' : '#374151',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                Strictly necessary only
              </button>
              <button
                className="cookie-button primary"
                onClick={handleSavePreferences}
                style={{
                  background: '#7C5832',
                  border: 'none',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#5d4025';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#7C5832';
                }}
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @media (max-width: 768px) {
          .cookie-policy-trigger {
            bottom: 75px !important;
            right: 15px !important;
            font-size: 11px !important;
            padding: 6px 10px !important;
          }
        }
      `}</style>
    </>
  );
};

export default CookiePolicyModal;
